import { Badge } from '../atoms/Badge/Badge';
import { Card } from '../atoms/Card/Card';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import { useAiUsage } from '../../hooks/useAiUsage';
import { AiLimitNotice, formatResetDate } from './AiLimitNotice';
import './AiUsageMeter.css';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';

export interface AiUsageMeterProps {
  /**
   * full: settings/account view with plan and explanation, plus loading and
   * error states. compact: one line next to AI features (renders nothing while
   * loading or when the usage cannot be read — the AI feature itself still
   * reports a refusal).
   */
  variant?: 'full' | 'compact';
  /** Wrap the populated meter in a Card (for placement directly on a page background). */
  framed?: boolean;
  className?: string;
}

function formatCredits(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(value);
}

/**
 * The user's AI credits this month: "AI credits: 37 / 200 this month · resets
 * 1 Nov", a progress bar, a warning Badge at ≥ 80 % and an explanation once the
 * credits are used up.
 */
export function AiUsageMeter({ variant = 'compact', framed = false, className = '' }: AiUsageMeterProps) {
  const { t, i18n } = useTranslation();
  const { usage, isLoading, error, retry } = useAiUsage();
  const rootClass = `rg-ai-usage-meter rg-ai-usage-meter--${variant} ${className}`.trim();

  if (isLoading) {
    if (variant === 'compact') return null;
    return (
      <LiveRegion busy className={rootClass}>
        <Text size="sm" color="muted">
          {t('aiUsage.loading')}
        </Text>
      </LiveRegion>
    );
  }

  if (error != null || usage === undefined) {
    if (variant === 'compact') return null;
    return (
      <LoadErrorState
        compact
        className={rootClass}
        title={t('aiUsage.loadError')}
        onRetry={retry}
        retryLabel={t('aiUsage.retry')}
      />
    );
  }

  const language = i18n.language;
  const used = formatCredits(usage.creditsUsed, language);
  const date = formatResetDate(usage.resetsAt, language) ?? '';
  const unlimited = usage.creditsBudget === null;
  const label = unlimited
    ? t('aiUsage.meterUnlimited', { used })
    : t('aiUsage.meter', { used, budget: formatCredits(usage.creditsBudget ?? 0, language), date });
  const percent = Math.max(0, Math.min(100, usage.percent));
  const tone = usage.exceeded ? 'danger' : usage.warning ? 'warning' : 'ok';
  // One-time welcome credit: part of the budget while its 30-day window lasts.
  const welcomeCredits = usage.welcomeCredits ?? 0;
  const welcomeUntil = formatResetDate(usage.welcomeExpiresAt, language);

  const meter = (
    <Stack gap="2" className={rootClass} data-testid="ai-usage-meter">
      <Inline gap="2" justify="between">
        <Text size="sm" weight="medium" as="span">
          {label}
        </Text>
        {usage.exceeded ? (
          <Badge tone="danger">{t('aiUsage.exceededBadge')}</Badge>
        ) : usage.warning ? (
          <Badge tone="warning">{t('aiUsage.warningBadge', { percent: Math.round(usage.percent) })}</Badge>
        ) : null}
      </Inline>

      {!unlimited && (
        <div
          className="rg-ai-usage-meter__track"
          role="progressbar"
          aria-label={t('aiUsage.progressLabel')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(percent)}
          aria-valuetext={label}
        >
          <div className={`rg-ai-usage-meter__fill rg-ai-usage-meter__fill--${tone}`} style={{ width: `${percent}%` }} />
        </div>
      )}

      {usage.exceeded && <AiLimitNotice kind="exceeded" resetsAt={usage.resetsAt} />}

      {variant === 'full' && (
        <Stack gap="1">
          <Text size="sm" color="secondary">
            {t('aiUsage.planLabel', { plan: t(`aiUsage.plan.${usage.plan}`) })}
          </Text>
          {usage.topupCredits > 0 && (
            <Text size="sm" color="secondary">
              {t('aiUsage.topupLabel', { credits: formatCredits(usage.topupCredits, language) })}
            </Text>
          )}
          {welcomeCredits > 0 && welcomeUntil !== null && (
            <Text size="sm" color="secondary">
              {t('aiUsage.welcomeLabel', {
                credits: formatCredits(welcomeCredits, language),
                remaining: formatCredits(usage.welcomeRemaining ?? welcomeCredits, language),
                date: welcomeUntil,
              })}
            </Text>
          )}
          <Text size="sm" color="muted">
            {t('aiUsage.explanation')}
          </Text>
        </Stack>
      )}
    </Stack>
  );

  return framed ? <Card padding="md">{meter}</Card> : meter;
}

export default AiUsageMeter;
